import { useEffect, useRef, useState } from "react";

// Parses a stat "value" string like "8,500+", "98%", "12+", "25+" into
// { numeric, prefix, suffix } so we can animate just the digits.
function parseStat(value) {
  const str = String(value).trim();
  // pull the leading number (supports commas + decimals)
  const match = str.match(/^([^\d.-]*)([\d,.]+)(.*)$/);
  if (!match) return { prefix: "", numeric: null, suffix: str };
  const [, prefix, num, suffix] = match;
  const parsed = parseFloat(num.replace(/,/g, ""));
  if (isNaN(parsed)) return { prefix: "", numeric: null, suffix: str };
  return { prefix, numeric: parsed, suffix };
}

function formatNumber(n, original) {
  // If original had commas, format with commas; otherwise use integer
  if (String(original).includes(",")) return Math.round(n).toLocaleString("en-US");
  if (String(original).includes(".")) return n.toFixed(1);
  return Math.round(n).toString();
}

export default function AnimatedStat({ value, label, delay = 0 }) {
  const parsed = parseStat(value);
  const [display, setDisplay] = useState(parsed.numeric == null ? value : `${parsed.prefix}0${parsed.suffix}`);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (parsed.numeric == null) return;
    const el = ref.current;
    if (!el) return;

    const run = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const target = parsed.numeric;
      const duration = 1400;
      const startTs = performance.now() + delay;
      const step = (ts) => {
        const t = Math.min(1, Math.max(0, (ts - startTs) / duration));
        const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
        const current = target * eased;
        setDisplay(`${parsed.prefix}${formatNumber(current, parsed.numeric)}${parsed.suffix}`);
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) run();
        });
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [parsed.numeric, parsed.prefix, parsed.suffix, delay]);

  return (
    <div ref={ref} className="text-center" data-testid="animated-stat">
      <div className="font-display text-5xl sm:text-6xl lg:text-7xl font-black gold-text mb-2 tabular-nums">
        {display}
      </div>
      <div className="text-xs sm:text-sm uppercase tracking-widest text-white/55">{label}</div>
    </div>
  );
}
